import React, { useState } from 'react';
import { ProcessTransition, ProcessRole } from '../../types/processDefinition';
import { ProcessDefinitionService } from '../../services/processDefinitionService';
import './TransitionCard.css';

interface TransitionCardProps {
  transition: ProcessTransition;
  role: ProcessRole;
  onUpdate: (transitionId: string, transitionData: Record<string, unknown>) => void;
  onDelete: (transitionId: string) => void;
  onNavigateToProcess: (processId: string) => void;
}

export const TransitionCard: React.FC<TransitionCardProps> = ({
  transition,
  role,
  onDelete,
  onNavigateToProcess,
}) => {
  const [showRelatedProcesses, setShowRelatedProcesses] = useState(false);
  const [relatedProcesses, setRelatedProcesses] = useState<{ id: string; name: string }[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const handleShowRelatedProcesses = async () => {
    if (showRelatedProcesses) {
      setShowRelatedProcesses(false);
      return;
    }

    try {
      setLoadingRelated(true);
      const processes = await ProcessDefinitionService.getProcessesUsingArtifactState(
        transition.prime_artifact_ref,
        transition.from_state_ref
      );
      setRelatedProcesses(processes);
      setShowRelatedProcesses(true);
    } catch (error) {
      console.error('Failed to load related processes:', error);
    } finally {
      setLoadingRelated(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this transition?')) {
      onDelete(transition.id);
    }
  };

  return (
    <div className="transition-card">
      <div className="transition-header">
        <div className="transition-info">
          <span className="transition-name">{transition.name}</span>
          <span className="transition-type">{role}</span>
        </div>
        <div className="transition-actions">
          <button
            onClick={handleShowRelatedProcesses}
            className="related-processes-btn"
            title="Show related processes"
            disabled={loadingRelated}
          >
            {loadingRelated ? '⏳' : '🔗'}
          </button>
          <button onClick={handleDelete} className="delete-btn" title="Delete transition">
            🗑️
          </button>
        </div>
      </div>

      <div className="transition-flow">
        <div className="state-container">
          <div className="state-box input-state">
            <span className="state-label">Input</span>
            <span className="state-name">{transition.from_state_name}</span>
            <button
              onClick={handleShowRelatedProcesses}
              className="state-navigation-btn"
              title="Show processes using this state"
            >
              🔗
            </button>
          </div>

          <div className="transition-arrow">
            <span className="arrow">→</span>
            <span className="transition-role">{role}</span>
          </div>

          <div className="state-box output-state">
            <span className="state-label">Output</span>
            <span className="state-name">{transition.to_state_name}</span>
            <button
              onClick={handleShowRelatedProcesses}
              className="state-navigation-btn"
              title="Show processes using this state"
            >
              🔗
            </button>
          </div>
        </div>
      </div>

      {transition.description && (
        <div className="transition-description">{transition.description}</div>
      )}

      {showRelatedProcesses && (
        <div className="related-processes">
          <h4>Related Processes</h4>
          {relatedProcesses.length === 0 ? (
            <p>No other processes use this artifact/state combination.</p>
          ) : (
            <ul>
              {relatedProcesses.map((process) => (
                <li key={process.id}>
                  <button
                    onClick={() => onNavigateToProcess(process.id)}
                    className="related-process-link"
                  >
                    {process.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
